import ArrowDownIcon from '@atlaskit/icon/core/arrow-down';
import ArrowUpIcon from '@atlaskit/icon/core/arrow-up';
import CopyIcon from '@atlaskit/icon/core/copy';
import CrossCircleIcon from '@atlaskit/icon/core/cross-circle';
import DeleteIcon from '@atlaskit/icon/core/delete';
import LinkIcon from '@atlaskit/icon/core/link';
import { type TriggerProps } from '@atlaskit/popup';
import { Flex, Inline, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import {
  getDatabaseContainerType,
  SendDatabaseDeleteEntryEventSources,
  sendDeleteEntryEvent,
  useAnalytics,
} from '@databases/analytics';
import ContextualMenu, {
  type MenuData,
  type MenuItem,
  useContextualMenu,
} from '@databases/contextual-menu';
import { copyToClipboard } from '@databases/copy-paste/copy-helper';
import {
  InlineRovoPromptTriggerSource,
  openInlineRovoPrompt,
} from '@databases/database-ai-first-creation-state/inline-rovo-prompt-state';
import { AskRovoIcon } from '@databases/database-ai-first-creation-ui/icons/ask-rovo-icon';
import { getBaseUrl } from '@databases/database-state';
import {
  ActiveVerticalDragHandlerIcon,
  CollapseIcon,
  ExpandIcon,
  InsertRowAboveIcon,
  InsertRowBelowIcon,
  VerticalDragHandlerIcon,
} from '@databases/icons';
import { messages } from '@databases/intl/messages';
import { SHORTCUTS, tooltip } from '@databases/keyboard-shortcuts';
import usePanel from '@databases/panel/usePanel';
import usePanelState from '@databases/panel/usePanelState';
import {
  type HoverProps,
  isModifierKeyPressed,
  useComponent,
  useKeyboardShortcut,
} from '@databases/react';
import { type DefaultRootState, defaultStoreUtils } from '@databases/redux';
import { setHoverDeleteButton } from '@databases/redux/cellSelections/actions';
import {
  isEntrySelected,
  selectHasEntryAfterSelection,
  selectHasEntryBeforeSelection,
  selectIsEntireEntrySelected,
  selectIsHoverDeleteEntriesButton,
  selectSelectedEntireEntryIds,
  selectSelectedEntriesCount,
} from '@databases/redux/cellSelections/selectors';
import {
  doClearEntireEntries,
  doDeleteEntries,
  doDuplicateEntireEntries,
  doInsertEntries,
  doMoveEntries,
  doSelectNewEntries,
} from '@databases/redux/contextual-menu/actions';
import { IGNORED_TABLE_ELEMENT_CLASS } from '@databases/table-resizer';
import { DatabaseLayoutType } from '@databases/types/database/view';
import { TABLE_CLASSNAMES } from '@databases/utils/constants';
import { getIsFilterOrSortingActive } from '@databases/utils/database/view/view';
import { useIsFeatureEnabled } from '@databases/utils/hooks/useIsFeatureEnabled';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import {
  getDatabaseLinkUrlWithQueryParams,
  getDatabasePath,
} from '@databases/utils/urls';
import useView from '@databases/views/useView';
import React, {
  type FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl-next';

import { getExperimentValue } from '@canvas/client-feature-flags';

import { DragHandlerWrapper, RowSelectButtonContainer } from './styles';
import { Position } from './type';

const elementBeforeIconWrapper = xcss({
  marginRight: 'space.negative.075',
});

const elementAfterShortCutWrapper = xcss({
  backgroundColor: 'color.background.neutral',
  color: 'color.text.subtlest',
  borderRadius: 'radius.xsmall',
  padding: 'space.050',
  font: 'font.body.small',
  alignSelf: 'flex-end',
});

enum SELECT_ENTRY_MENU_ACTIONS {
  ASK_ROVO = 'ask_rovo',
  EXPAND_ENTRY = 'expand_entry',
  COLLAPSE_ENTRY = 'collapse_entry',
  COPY_ENTRY_LINK = 'copy_entry_link',
  ADD_ENTRY_ABOVE = 'add_entry_above',
  ADD_ENTRY_BELOW = 'add_entry_below',
  DUPLICATE_ENTRIES = 'duplicate_entries',
  CLEAR_CELLS = 'clear_cells',
  DELETE_ENTRIES = 'delete_entries',
  MOVE_ENTRIES_UP = 'move_entries_up',
  MOVE_ENTRIES_DOWN = 'move_entries_down',
}

type SelectEntryMenuProps = {
  contentId: string;
  entryId: string;
  currentViewId?: string;
  hoverProps?: HoverProps;
  handleSelectEntry: (e: React.MouseEvent) => void;
  canEditContent: boolean;
  trigger?: (triggerProps: TriggerProps) => React.ReactNode;
  isActionBarEnabled?: boolean;
};

const SelectEntryMenu: FunctionComponent<SelectEntryMenuProps> = ({
  contentId,
  entryId,
  currentViewId,
  hoverProps,
  handleSelectEntry,
  canEditContent,
  trigger,
  isActionBarEnabled,
}) => {
  const {
    view: { settings: viewSettings },
  } = useView();
  const isDBAiFirstCreationEnabled = useIsFeatureEnabled('dbAiFirstCreation');
  const baseUrl = getBaseUrl();

  const [
    canAskRovo,
    canDeleteEntry,
    canDuplicateEntry,
    canInsertEntry,
    canMoveUp,
    canMoveDown,
    isSelected,
    oneSelectedEntry,
    canCopyValue,
    selectedEntriesCount,
    databasePath,
    isHoverDeleteEntriesButton,
    isReorderDisabled,
  ] = defaultStoreUtils.useEqualSelector((state: DefaultRootState) => {
    const database = selectDatabaseByContentIdRequired(state, {
      contentId,
    });
    const isSelected = selectIsEntireEntrySelected(state, {
      entryId,
    });
    const selectedEntireEntryIds = isSelected
      ? selectSelectedEntireEntryIds(state, {
          contentId,
        })
      : [];
    const oneSelectedEntry =
      selectedEntireEntryIds.length > 1 ? null : selectedEntireEntryIds[0];

    const selectedEntriesCount = isSelected
      ? selectSelectedEntriesCount(state, {
          contentId,
        })
      : 0;
    const hasEntryBeforeSelection =
      isSelected &&
      selectHasEntryBeforeSelection(state, {
        contentId,
      });
    const hasEntryAfterSelection =
      isSelected &&
      selectHasEntryAfterSelection(state, {
        contentId,
      });
    const isHoverDeleteEntriesButton =
      isSelected && selectIsHoverDeleteEntriesButton(state);

    const isFilterOrSortingActive = getIsFilterOrSortingActive(viewSettings);

    const canAskRovo =
      isDBAiFirstCreationEnabled && canEditContent && selectedEntriesCount > 0;
    const canInsertEntry = canEditContent && selectedEntriesCount > 0;
    const canDuplicateEntry = canEditContent && selectedEntriesCount > 0;
    const canDeleteEntry = canEditContent && selectedEntriesCount > 0;
    const canCopyValue = selectedEntriesCount === 1;
    const canMoveUp =
      canEditContent && selectedEntriesCount > 0 && hasEntryBeforeSelection;
    const canMoveDown =
      canEditContent && selectedEntriesCount > 0 && hasEntryAfterSelection;

    const isReorderDisabled = isFilterOrSortingActive;

    return [
      canAskRovo,
      canDeleteEntry,
      canDuplicateEntry,
      canInsertEntry,
      canMoveUp,
      canMoveDown,
      isSelected,
      oneSelectedEntry,
      canCopyValue,
      selectedEntriesCount,
      getDatabasePath({
        spaceKey: database.space.key,
        contentId,
      }),
      isHoverDeleteEntriesButton,
      isReorderDisabled,
    ] as const;
  });

  const { formatMessage } = useIntl();
  const store = defaultStoreUtils.useStore();
  const dispatch = defaultStoreUtils.useDispatch();
  const client = useAnalytics();

  const { isPanelVisible, getData } = usePanelState();
  const { showPanel, hidePanel } = usePanel();
  const { entryId: panelEntryId } = getData('EntryViewPanel') || {};

  const handleCollapseEntry = useCallback(() => {
    hidePanel();
  }, [hidePanel]);

  const handleExpandEntry = useCallback(() => {
    showPanel('EntryViewPanel', {
      entryId,
      isReadonly: !canEditContent,
    });
  }, [canEditContent, entryId, showPanel]);

  const menuData: MenuData = {
    type: 'entry',
    entryId,
  };
  const { isMenuOpen, toggleOpenMenu } = useContextualMenu(menuData);
  const prevIsSelected = useRef(isSelected);
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      toggleOpenMenu();
      if (!isSelected) {
        handleSelectEntry(e);
        prevIsSelected.current = true;
      } else {
        prevIsSelected.current = false;
      }
    },
    [handleSelectEntry, isSelected, toggleOpenMenu]
  );

  const handleMouseUp = useCallback(
    (e: React.MouseEvent) => {
      const isMultiSelectOn = isModifierKeyPressed(e, ['shiftKey']);
      if (isSelected && !prevIsSelected.current && !isMenuOpen) {
        // remove selection if the user clicks on the select button again
        handleSelectEntry(e);
      } else if (!isMultiSelectOn && isPanelVisible('EntryViewPanel')) {
        // open the entry dialog only if the dialog has already been opened from another entry
        handleExpandEntry();
      }
    },
    [handleExpandEntry, handleSelectEntry, isMenuOpen, isPanelVisible, isSelected]
  );

  const handleRightClick = (e: React.MouseEvent) => e.preventDefault();

  const defaultTrigger = useComponent(
    ({ ref, ...triggerProps }: TriggerProps) => (
      <RowSelectButtonContainer
        className={IGNORED_TABLE_ELEMENT_CLASS}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onContextMenu={handleRightClick}
        {...triggerProps}
        {...hoverProps}
        ref={ref}
        data-testid={'row-select-button'}
        aria-expanded={isMenuOpen}
        aria-haspopup="menu"
        role="button"
      >
        <DragHandlerWrapper
          className={TABLE_CLASSNAMES.SELECT_BUTTON}
          isActive={isMenuOpen && isSelected}
          isDeletionActive={isHoverDeleteEntriesButton}
          data-interactive
        >
          <div className={TABLE_CLASSNAMES.ACTIVE_DRAG_HANDLER_ICON}>
            <ActiveVerticalDragHandlerIcon
              label={TABLE_CLASSNAMES.ACTIVE_DRAG_HANDLER_ICON}
            />
          </div>
          <div className={TABLE_CLASSNAMES.DRAG_HANDLER_ICON}>
            <VerticalDragHandlerIcon label={TABLE_CLASSNAMES.DRAG_HANDLER_ICON} />
          </div>
        </DragHandlerWrapper>
      </RowSelectButtonContainer>
    ),
    [
      handleMouseDown,
      handleMouseUp,
      hoverProps,
      isMenuOpen,
      isSelected,
      isHoverDeleteEntriesButton,
    ]
  );

  const triggerComp = trigger ?? defaultTrigger;

  const [isCopied, setIsCopied] = useState(false);

  const handleCopyEntryLink = useCallback(() => {
    const entryLinkCopyUrl = getDatabaseLinkUrlWithQueryParams({
      baseUrl: baseUrl,
      databasePath: databasePath,
      contentId: contentId,
      entryId: entryId,
      viewId: currentViewId,
    });

    copyToClipboard(entryLinkCopyUrl.toString(), 'text/plain');
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 3000);
  }, [baseUrl, contentId, currentViewId, databasePath, entryId]);

  const handleInsertEntries = useCallback(
    async (position: Position) => {
      await dispatch(
        doSelectNewEntries(contentId, () =>
          dispatch(doInsertEntries(contentId, viewSettings, position === Position.BEFORE))
        )
      );
      await client.sendInsertRowEvent({
        contentId: contentId,
        databaseContainer: getDatabaseContainerType(),
      });
    },
    [client, contentId, dispatch, viewSettings]
  );

  const handleDuplicateEntries = useCallback(() => {
    dispatch(
      doSelectNewEntries(contentId, () => dispatch(doDuplicateEntireEntries(contentId)))
    );
  }, [dispatch, contentId]);

  const handleClearEntries = useCallback(() => {
    dispatch(doClearEntireEntries(contentId));
  }, [contentId, dispatch]);

  const handleDeleteEntries = useCallback(() => {
    if (isEntrySelected(store.getState(), contentId, panelEntryId)) {
      hidePanel();
    }

    dispatch(doDeleteEntries(contentId));
    dispatch(setHoverDeleteButton({ value: null }));
    void sendDeleteEntryEvent(
      client,
      SendDatabaseDeleteEntryEventSources.ENTRY_CONTEXTUAL_MENU,
      store.getState(),
      contentId
    );
  }, [store, contentId, panelEntryId, dispatch, client, hidePanel]);

  const handleMoveEntries = useCallback(
    (position: Position) => {
      dispatch(doMoveEntries(contentId, position === Position.BEFORE));
    },
    [contentId, dispatch]
  );

  const RovoElemBefore = () => {
    useEffect(() => {
      getExperimentValue('databasesAIFC', 'enabled', {
        fireExperimentExposure: true,
      });
    }, []);
    return (
      <Flex xcss={elementBeforeIconWrapper}>
        <AskRovoIcon height={16} width={16} />
      </Flex>
    );
  };

  const askRovoMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_ENTRY_MENU_ACTIONS.ASK_ROVO,
      content: formatMessage(messages['value-actions.ask-rovo']),
      onClick: () =>
        openInlineRovoPrompt(
          getInlineRovoPromptTriggerSourceForViewType(viewSettings.layout.type)
        ),
      closeOnClick: true,
      elemBefore: <RovoElemBefore />,
    }),
    [formatMessage, viewSettings.layout.type]
  );

  const expandEntryMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_ENTRY_MENU_ACTIONS.EXPAND_ENTRY,
      content: formatMessage(messages['entry-actions.expand']),
      onClick: handleExpandEntry,
      closeOnClick: true,
      elemBefore: (
        <Flex xcss={elementBeforeIconWrapper}>
          <ExpandIcon label="" />
        </Flex>
      ),
    }),
    [formatMessage, handleExpandEntry]
  );

  const collapseEntryMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_ENTRY_MENU_ACTIONS.COLLAPSE_ENTRY,
      content: formatMessage(messages['entry-actions.collapse']),
      onClick: handleCollapseEntry,
      closeOnClick: true,
      elemBefore: (
        <Flex xcss={elementBeforeIconWrapper}>
          <CollapseIcon label="" size="medium" />
        </Flex>
      ),
    }),
    [formatMessage, handleCollapseEntry]
  );

  const copyEntryLinkMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_ENTRY_MENU_ACTIONS.COPY_ENTRY_LINK,
      content: formatMessage(messages['entry-actions.copy-link']),
      onClick: handleCopyEntryLink,
      tooltipDescription: isCopied
        ? formatMessage(messages['copy-link.tooltip'])
        : undefined,
      tooltipPosition: 'right',
      elemBefore: (
        <Inline
          spread="space-between"
          alignBlock="center"
          xcss={elementBeforeIconWrapper}
        >
          <LinkIcon label="" spacing="spacious" />
        </Inline>
      ),
    }),
    [formatMessage, handleCopyEntryLink, isCopied]
  );

  const addEntryAboveMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_ENTRY_MENU_ACTIONS.ADD_ENTRY_ABOVE,
      content: formatMessage(messages['entry-actions.add-above']),
      onClick: () => handleInsertEntries(Position.BEFORE),
      closeOnClick: true,
      elemAfter: (
        <Inline xcss={elementAfterShortCutWrapper}>
          {tooltip(SHORTCUTS.ADD_ENTRY_ABOVE.keyMap)}
        </Inline>
      ),
      elemBefore: (
        <Flex xcss={elementBeforeIconWrapper}>
          <InsertRowAboveIcon label="" size="medium" />
        </Flex>
      ),
    }),
    [formatMessage, handleInsertEntries]
  );

  const addEntryBelowMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_ENTRY_MENU_ACTIONS.ADD_ENTRY_BELOW,
      content: formatMessage(messages['entry-actions.add-below']),
      onClick: () => handleInsertEntries(Position.AFTER),
      closeOnClick: true,
      elemAfter: (
        <Inline xcss={elementAfterShortCutWrapper}>
          {tooltip(SHORTCUTS.ADD_ENTRY_BELOW.keyMap)}
        </Inline>
      ),
      elemBefore: (
        <Flex xcss={elementBeforeIconWrapper}>
          <InsertRowBelowIcon label="" size="medium" />
        </Flex>
      ),
    }),
    [formatMessage, handleInsertEntries]
  );

  const duplicateEntriesMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_ENTRY_MENU_ACTIONS.DUPLICATE_ENTRIES,
      content: formatMessage(messages['entry-actions.duplicate-entry'], {
        entryCount: selectedEntriesCount,
      }),
      onClick: handleDuplicateEntries,
      closeOnClick: true,
      elemAfter: (
        <Inline xcss={elementAfterShortCutWrapper}>
          {tooltip(SHORTCUTS.DUPLICATE.keyMap)}
        </Inline>
      ),
      elemBefore: (
        <Flex xcss={elementBeforeIconWrapper}>
          <CopyIcon label="" spacing="spacious" />
        </Flex>
      ),
    }),
    [formatMessage, handleDuplicateEntries, selectedEntriesCount]
  );

  const clearCellsMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_ENTRY_MENU_ACTIONS.CLEAR_CELLS,
      content: formatMessage(messages['value-actions.clear-cells'], { valuesCount: 2 }),
      onClick: handleClearEntries,
      closeOnClick: true,
      elemAfter: (
        <Inline xcss={elementAfterShortCutWrapper}>
          {tooltip(SHORTCUTS.CLEAR.keyMap)}
        </Inline>
      ),
      elemBefore: (
        <Flex xcss={elementBeforeIconWrapper}>
          <CrossCircleIcon label="" spacing="spacious" />
        </Flex>
      ),
    }),
    [formatMessage, handleClearEntries]
  );

  const onHoverDeleteFieldButton = useCallback(() => {
    dispatch(setHoverDeleteButton({ value: 'entries' }));
  }, [dispatch]);

  const onLeaveDeleteFieldButton = useCallback(() => {
    dispatch(setHoverDeleteButton({ value: null }));
  }, [dispatch]);

  const deleteEntriesMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_ENTRY_MENU_ACTIONS.DELETE_ENTRIES,
      content: formatMessage(messages['entry-actions.delete'], {
        entryCount: selectedEntriesCount,
      }),
      onClick: handleDeleteEntries,
      onMouseEnter: onHoverDeleteFieldButton,
      onMouseLeave: onLeaveDeleteFieldButton,
      closeOnClick: true,
      elemAfter: (
        <Inline xcss={elementAfterShortCutWrapper}>
          {tooltip(SHORTCUTS.DELETE.keyMap)}
        </Inline>
      ),
      elemBefore: (
        <Flex xcss={elementBeforeIconWrapper}>
          <DeleteIcon
            label=""
            spacing="spacious"
            color={
              isHoverDeleteEntriesButton
                ? token('color.icon.danger', colors.R500)
                : undefined
            }
          />
        </Flex>
      ),
    }),
    [
      formatMessage,
      handleDeleteEntries,
      isHoverDeleteEntriesButton,
      onHoverDeleteFieldButton,
      onLeaveDeleteFieldButton,
      selectedEntriesCount,
    ]
  );

  const moveEntriesUpMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_ENTRY_MENU_ACTIONS.MOVE_ENTRIES_UP,
      content: formatMessage(messages['entry-actions.move-up'], {
        entryCount: selectedEntriesCount,
      }),
      onClick: () => handleMoveEntries(Position.BEFORE),
      closeOnClick: true,
      isDisabled: isReorderDisabled,
      elemAfter: (
        <Inline xcss={elementAfterShortCutWrapper}>
          {tooltip(SHORTCUTS.MOVE_ENTRIES_UP.keyMap)}
        </Inline>
      ),
      elemBefore: (
        <Flex xcss={elementBeforeIconWrapper}>
          <ArrowUpIcon label="" spacing="spacious" />
        </Flex>
      ),
    }),
    [formatMessage, handleMoveEntries, isReorderDisabled, selectedEntriesCount]
  );

  const moveEntriesDownMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_ENTRY_MENU_ACTIONS.MOVE_ENTRIES_DOWN,
      content: formatMessage(messages['entry-actions.move-down'], {
        entryCount: selectedEntriesCount,
      }),
      onClick: () => handleMoveEntries(Position.AFTER),
      closeOnClick: true,
      isDisabled: isReorderDisabled,
      elemAfter: (
        <Inline xcss={elementAfterShortCutWrapper}>
          {tooltip(SHORTCUTS.MOVE_ENTRIES_DOWN.keyMap)}
        </Inline>
      ),
      elemBefore: (
        <Flex xcss={elementBeforeIconWrapper}>
          <ArrowDownIcon label="" spacing="spacious" />
        </Flex>
      ),
    }),
    [formatMessage, handleMoveEntries, isReorderDisabled, selectedEntriesCount]
  );
  const menuItems = useMemo(() => {
    const isEntryPanelActive = isPanelVisible('EntryViewPanel');
    const firstGroup: MenuItem[] = [];
    const secondGroup: MenuItem[] = [];
    const thirdGroup: MenuItem[] = [];
    const fourthGroup: MenuItem[] = [];

    if (
      canAskRovo &&
      getExperimentValue('databasesAIFC', 'enabled', {
        fireExperimentExposure: false,
      })
    ) {
      firstGroup.push(askRovoMenuItem);
    }
    if (oneSelectedEntry && !isEntryPanelActive) {
      secondGroup.push(expandEntryMenuItem);
    }
    if (oneSelectedEntry && isEntryPanelActive) {
      secondGroup.push(collapseEntryMenuItem);
    }
    if (canCopyValue) {
      secondGroup.push(copyEntryLinkMenuItem);
    }
    if (canInsertEntry) {
      thirdGroup.push(addEntryAboveMenuItem);
      thirdGroup.push(addEntryBelowMenuItem);
    }
    if (canDuplicateEntry) {
      thirdGroup.push(duplicateEntriesMenuItem);
    }
    if (canDeleteEntry) {
      thirdGroup.push(clearCellsMenuItem);
      thirdGroup.push(deleteEntriesMenuItem);
    }
    if (canMoveUp) {
      fourthGroup.push(moveEntriesUpMenuItem);
    }
    if (canMoveDown) {
      fourthGroup.push(moveEntriesDownMenuItem);
    }
    return [
      { items: firstGroup },
      { items: secondGroup },
      { items: thirdGroup },
      { items: fourthGroup },
    ];
  }, [
    askRovoMenuItem,
    canMoveDown,
    canMoveUp,
    moveEntriesDownMenuItem,
    moveEntriesUpMenuItem,
    addEntryAboveMenuItem,
    addEntryBelowMenuItem,
    canAskRovo,
    canCopyValue,
    canDeleteEntry,
    canDuplicateEntry,
    canInsertEntry,
    clearCellsMenuItem,
    collapseEntryMenuItem,
    copyEntryLinkMenuItem,
    deleteEntriesMenuItem,
    duplicateEntriesMenuItem,
    expandEntryMenuItem,
    isPanelVisible,
    oneSelectedEntry,
  ]);

  useKeyboardShortcut(
    isMenuOpen && canInsertEntry ? SHORTCUTS.ADD_ENTRY_ABOVE.keyboardShortCut : undefined,
    () => handleInsertEntries(Position.BEFORE)
  );
  useKeyboardShortcut(
    isMenuOpen && canInsertEntry ? SHORTCUTS.ADD_ENTRY_BELOW.keyboardShortCut : undefined,
    () => handleInsertEntries(Position.AFTER)
  );
  useKeyboardShortcut(
    isMenuOpen && canDuplicateEntry ? SHORTCUTS.DUPLICATE.keyboardShortCut : undefined,
    handleDuplicateEntries
  );
  useKeyboardShortcut(
    isMenuOpen && canDeleteEntry ? SHORTCUTS.CLEAR.keyboardShortCut : undefined,
    handleClearEntries
  );
  useKeyboardShortcut(
    isMenuOpen && canDeleteEntry ? SHORTCUTS.DELETE.keyboardShortCut : undefined,
    handleDeleteEntries
  );
  useKeyboardShortcut(
    isMenuOpen && canMoveUp ? SHORTCUTS.MOVE_ENTRIES_UP.keyboardShortCut : undefined,
    () => handleMoveEntries(Position.BEFORE)
  );
  useKeyboardShortcut(
    isMenuOpen && canMoveDown ? SHORTCUTS.MOVE_ENTRIES_DOWN.keyboardShortCut : undefined,
    () => handleMoveEntries(Position.AFTER)
  );

  return (
    <ContextualMenu
      menuData={menuData}
      items={menuItems}
      trigger={triggerComp}
      isActionBarEnabled={isActionBarEnabled}
    />
  );
};

function getInlineRovoPromptTriggerSourceForViewType(
  viewType: DatabaseLayoutType
): InlineRovoPromptTriggerSource {
  switch (viewType) {
    case DatabaseLayoutType.BOARD:
      return InlineRovoPromptTriggerSource.BoardEntry;
    case DatabaseLayoutType.GALLERY:
      return InlineRovoPromptTriggerSource.GalleryEntry;
    case DatabaseLayoutType.TABLE:
    default:
      return InlineRovoPromptTriggerSource.TableEntry;
  }
}

export default SelectEntryMenu;
